/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import apiService from "@/services/apiService";
import useErrorStore from "@/store/confirmationStore";
import { showValidationToast } from "@/lib/toastHelper";
import { validateDate, validateNumber, validateRequired } from "@/lib/validationHelper";
import { getUserInfo } from "@/services/userService";

export interface Crpc129NoticeForm {
  id?: string;
  courtName: string;
  caseNumber: string;
  issuingOfficer: string;
  recipientName: string;
  recipientFatherName: string;
  recipientAge: string;
  recipientAddress: string;
  noticeType: string;
  noticeContent: string;
  courtOrder: string;
  appearanceDate: string; // will store DateTime as ISO string
  appearanceTime: string;
  issueDate: string; // will store DateTime as ISO string
  magistrateSignature: string;
  description: string;
  boundDownOrderId?: string;
  processedForBnsl?: boolean;
  boundDownOrder?: any;
  file_name?: string;
  parentDocumentId?: string;
  document?: any;
  documentType?: string; // e.g., '111' for CrPC 129 Notice
  created_by?: string;
  createdBy?: any;
  parent?: any;
}

export interface BoundDownOrderCreate {
  meaning: string;
  dateOfOrder: string;
  duration: string;
  bondAmount: number;
  amountPerSurety: number;
  totalSureties: number;
  suretyConditions: string;
  consequenceOnDefault: string;
  summary: string;
  effectiveFrom: string;
  effectiveTo: string;
}

interface Crpc129NoticePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: Partial<Crpc129NoticeForm>;
}

export function Crpc129NoticePopup({ open, onOpenChange, data }: Crpc129NoticePopupProps) {
  const showError = useErrorStore((s) => s.showError);
  const [formData, setFormData] = useState<Partial<Crpc129NoticeForm>>({});
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Validation functions
  

  const validateForm = (): boolean => {
    const errors: any = {};
    let hasErrors = false;
    // Required fields
    const requiredFields = ["courtName", "caseNumber", "issuingOfficer", "recipientName", "recipientFatherName", "recipientAge", "recipientAddress", "noticeType", "noticeContent", "courtOrder", "appearanceDate", "appearanceTime", "issueDate", "magistrateSignature", "description"];
    requiredFields.forEach((field) => {
      let error = validateRequired(
        formData[field as keyof Crpc129NoticeForm],
        field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
      );
      if (["appearanceDate", "issueDate"].includes(field))
        error = validateDate(
          formData[field as keyof Crpc129NoticeForm],
          field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
        );
      if (field === "recipientAge") error = validateNumber(formData[field], "Recipient Age", 0);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    // BoundDownOrder validation
    if (formData.boundDownOrder) {
      errors.boundDownOrder = {};
      const bdo = formData.boundDownOrder;
      const bdoFields = ["meaning", "dateOfOrder", "duration", "bondAmount", "amountPerSurety", "totalSureties", "suretyConditions", "consequenceOnDefault", "summary", "effectiveFrom", "effectiveTo"];
      bdoFields.forEach((field) => {
        let error = validateRequired(
          bdo[field],
          field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
        );
        if (["dateOfOrder", "effectiveFrom", "effectiveTo"].includes(field))
          error = validateDate(
            bdo[field],
            field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
          );
        if (["bondAmount", "amountPerSurety", "totalSureties"].includes(field))
          error = validateNumber(
            bdo[field],
            field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
            field === "totalSureties" ? 1 : 0
          );
        if (error) {
          errors.boundDownOrder[field] = error;
          hasErrors = true;
        }
      });
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  const getFieldError = (field: string): string => {
    return validationErrors[field] || "";
  };
  const getBoundDownOrderError = (field: string): string => {
    return validationErrors.boundDownOrder?.[field] || "";
  };

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleInputChange = (field: keyof Crpc129NoticeForm, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBoundDownOrderChange = (key: keyof BoundDownOrderCreate, value: any) => {
    setFormData((prev) => ({
      ...prev,
      boundDownOrder: {
        meaning: prev.boundDownOrder?.meaning ?? "",
        dateOfOrder: prev.boundDownOrder?.dateOfOrder ?? "",
        duration: prev.boundDownOrder?.duration ?? "",
        bondAmount: prev.boundDownOrder?.bondAmount ?? 0,
        amountPerSurety: prev.boundDownOrder?.amountPerSurety ?? 0,
        totalSureties: prev.boundDownOrder?.totalSureties ?? 0,
        suretyConditions: prev.boundDownOrder?.suretyConditions ?? "",
        consequenceOnDefault: prev.boundDownOrder?.consequenceOnDefault ?? "",
        summary: prev.boundDownOrder?.summary ?? "",
        effectiveFrom: prev.boundDownOrder?.effectiveFrom ?? "",
        effectiveTo: prev.boundDownOrder?.effectiveTo ?? "",
        [key]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!validateForm()) {
      showValidationToast(validationErrors);
      return;
    }
    // ...existing code...
    const postParams = JSON.parse(JSON.stringify(formData));
    postParams.appearanceDate = postParams.appearanceDate ? new Date(postParams.appearanceDate).toISOString() : "";
    postParams.issueDate = postParams.issueDate ? new Date(postParams.issueDate).toISOString() : "";
    postParams.boundDownOrder.dateOfOrder = postParams.boundDownOrder.dateOfOrder ? new Date(postParams.boundDownOrder.dateOfOrder).toISOString() : "";
    postParams.boundDownOrder.effectiveFrom = postParams.boundDownOrder.effectiveFrom ? new Date(postParams.boundDownOrder.effectiveFrom).toISOString() : null;
    postParams.boundDownOrder.effectiveTo = postParams.boundDownOrder.effectiveTo ? new Date(postParams.boundDownOrder.effectiveTo).toISOString() : null;
    postParams.document = {
      connect: { id: postParams.parentDocumentId || "" },
    };
    delete postParams.file_name;
    delete postParams.parentDocumentId;
    delete postParams.documentType;
    delete postParams.created_by;
    delete postParams.parent;

    const userInfo:any = getUserInfo();
    if (userInfo) {
      postParams.createdBy = {
        connect: { id: userInfo.id },
      };
      postParams.boundDownOrder.createdBy = {
        connect: { id: userInfo.id },
      };
    }

    const payload = {
      ...postParams,
      recipientAge: postParams.recipientAge ? String(postParams.recipientAge) : "",
      boundDownOrder: { create: postParams.boundDownOrder },
    };

    const response = await apiService.post('crpc129-notice',JSON.stringify(payload));
    try {
      if (response) {
        toast.success("CrPC 129 Notice saved successfully!", {
          description: "The notice has been saved.",
          action: { label: "Ok", onClick: () => console.log("Ok") },
        });
        onOpenChange(false);
      } else {
        showError("Failed to save notice. Please check the form and try again.");
      }
    } catch(error:any){
      showError(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[95vh] bg-white flex flex-col w-[95%]" style={{ maxWidth: "1200px" }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-black">CrPC 129 Notice</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          {/* Court & Case Info */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Court & Case Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Court Name</Label>
                <Input disabled={isPreviewMode} value={formData.courtName || ""} onChange={(e) => handleInputChange("courtName", e.target.value)} className={showErrors && getFieldError("courtName") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("courtName")}</div>}
              </div>
              <div>
                <Label>Case Number</Label>
                <Input disabled={isPreviewMode} value={formData.caseNumber || ""} onChange={(e) => handleInputChange("caseNumber", e.target.value)} className={showErrors && getFieldError("caseNumber") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("caseNumber")}</div>}
              </div>
              <div>
                <Label>Issuing Officer</Label>
                <Input disabled={isPreviewMode} value={formData.issuingOfficer || ""} onChange={(e) => handleInputChange("issuingOfficer", e.target.value)} className={showErrors && getFieldError("issuingOfficer") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("issuingOfficer")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Recipient Info */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Recipient Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input disabled={isPreviewMode} value={formData.recipientName || ""} onChange={(e) => handleInputChange("recipientName", e.target.value)} className={showErrors && getFieldError("recipientName") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("recipientName")}</div>}
              </div>
              <div>
                <Label>Father's Name</Label>
                <Input disabled={isPreviewMode} value={formData.recipientFatherName || ""} onChange={(e) => handleInputChange("recipientFatherName", e.target.value)} className={showErrors && getFieldError("recipientFatherName") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("recipientFatherName")}</div>}
              </div>
              <div>
                <Label>Age</Label>
                <Input disabled={isPreviewMode} value={formData.recipientAge || ""} onChange={(e) => handleInputChange("recipientAge", e.target.value)} className={showErrors && getFieldError("recipientAge") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("recipientAge")}</div>}
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea disabled={isPreviewMode} value={formData.recipientAddress || ""} onChange={(e) => handleInputChange("recipientAddress", e.target.value)} className={showErrors && getFieldError("recipientAddress") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("recipientAddress")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Notice Details */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Notice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Notice Type</Label>
                <Input disabled={isPreviewMode} value={formData.noticeType || ""} onChange={(e) => handleInputChange("noticeType", e.target.value)} className={showErrors && getFieldError("noticeType") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("noticeType")}</div>}
              </div>
              <div>
                <Label>Notice Content</Label>
                <Textarea disabled={isPreviewMode} rows={3} value={formData.noticeContent || ""} onChange={(e) => handleInputChange("noticeContent", e.target.value)} className={showErrors && getFieldError("noticeContent") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("noticeContent")}</div>}
              </div>
              <div>
                <Label>Court Order</Label>
                <Textarea disabled={isPreviewMode} rows={3} value={formData.courtOrder || ""} onChange={(e) => handleInputChange("courtOrder", e.target.value)} className={showErrors && getFieldError("courtOrder") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("courtOrder")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Bound Down Order */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Bound Down Order</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Meaning</Label>
                <Input disabled={isPreviewMode} value={formData.boundDownOrder?.meaning || ""} onChange={(e) => handleBoundDownOrderChange("meaning", e.target.value)} className={showErrors && getBoundDownOrderError("meaning") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("meaning")}</div>}
              </div>
              <div>
                <Label>Date of Order</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.boundDownOrder?.dateOfOrder || ""} onChange={(e) => handleBoundDownOrderChange("dateOfOrder", e.target.value)} className={showErrors && getBoundDownOrderError("dateOfOrder") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("dateOfOrder")}</div>}
              </div>
              <div>
                <Label>Duration</Label>
                <Input disabled={isPreviewMode} value={formData.boundDownOrder?.duration || ""} onChange={(e) => handleBoundDownOrderChange("duration", e.target.value)} className={showErrors && getBoundDownOrderError("duration") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("duration")}</div>}
              </div>
              <div>
                <Label>Bond Amount</Label>
                <Input disabled={isPreviewMode} type="number" value={formData.boundDownOrder?.bondAmount || ""} onChange={(e) => handleBoundDownOrderChange("bondAmount", Number(e.target.value))} className={showErrors && getBoundDownOrderError("bondAmount") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("bondAmount")}</div>}
              </div>
              <div>
                <Label>Amount Per Surety</Label>
                <Input disabled={isPreviewMode} type="number" value={formData.boundDownOrder?.amountPerSurety || ""} onChange={(e) => handleBoundDownOrderChange("amountPerSurety", Number(e.target.value))} className={showErrors && getBoundDownOrderError("amountPerSurety") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("amountPerSurety")}</div>}
              </div>
              <div>
                <Label>Total Sureties</Label>
                <Input disabled={isPreviewMode} type="number" value={formData.boundDownOrder?.totalSureties || ""} onChange={(e) => handleBoundDownOrderChange("totalSureties", Number(e.target.value))} className={showErrors && getBoundDownOrderError("totalSureties") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("totalSureties")}</div>}
              </div>
              <div className="md:col-span-2">
                <Label>Surety Conditions</Label>
                <Textarea disabled={isPreviewMode} value={formData.boundDownOrder?.suretyConditions || ""} onChange={(e) => handleBoundDownOrderChange("suretyConditions", e.target.value)} className={showErrors && getBoundDownOrderError("suretyConditions") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("suretyConditions")}</div>}
              </div>
              <div className="md:col-span-2">
                <Label>Consequence on Default</Label>
                <Textarea disabled={isPreviewMode} value={formData.boundDownOrder?.consequenceOnDefault || ""} onChange={(e) => handleBoundDownOrderChange("consequenceOnDefault", e.target.value)} className={showErrors && getBoundDownOrderError("consequenceOnDefault") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("consequenceOnDefault")}</div>}
              </div>
              <div className="md:col-span-2">
                <Label>Summary</Label>
                <Textarea disabled={isPreviewMode} value={formData.boundDownOrder?.summary || ""} onChange={(e) => handleBoundDownOrderChange("summary", e.target.value)} className={showErrors && getBoundDownOrderError("summary") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("summary")}</div>}
              </div>
              <div>
                <Label>Effective From</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.boundDownOrder?.effectiveFrom || ""} onChange={(e) => handleBoundDownOrderChange("effectiveFrom", e.target.value)} className={showErrors && getBoundDownOrderError("effectiveFrom") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("effectiveFrom")}</div>}
              </div>
              <div>
                <Label>Effective To</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.boundDownOrder?.effectiveTo || ""} onChange={(e) => handleBoundDownOrderChange("effectiveTo", e.target.value)} className={showErrors && getBoundDownOrderError("effectiveTo") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getBoundDownOrderError("effectiveTo")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Dates & Signatures */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Dates & Signatures</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Appearance Date</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.appearanceDate || ""} onChange={(e) => handleInputChange("appearanceDate", e.target.value)} className={showErrors && getFieldError("appearanceDate") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("appearanceDate")}</div>}
              </div>
              <div>
                <Label>Appearance Time</Label>
                <Input disabled={isPreviewMode} type="time" value={formData.appearanceTime || ""} onChange={(e) => handleInputChange("appearanceTime", e.target.value)} className={showErrors && getFieldError("appearanceTime") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("appearanceTime")}</div>}
              </div>
              <div>
                <Label>Issue Date</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.issueDate || ""} onChange={(e) => handleInputChange("issueDate", e.target.value)} className={showErrors && getFieldError("issueDate") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("issueDate")}</div>}
              </div>
              <div>
                <Label>Magistrate Signature</Label>
                <Input disabled={isPreviewMode} value={formData.magistrateSignature || ""} onChange={(e) => handleInputChange("magistrateSignature", e.target.value)} className={showErrors && getFieldError("magistrateSignature") ? "border-red-500" : ""} />
                {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("magistrateSignature")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea disabled={isPreviewMode} rows={3} value={formData.description || ""} onChange={(e) => handleInputChange("description", e.target.value)} className={showErrors && getFieldError("description") ? "border-red-500" : ""} />
              {showErrors && <div className="text-red-600 text-sm mt-1">{getFieldError("description")}</div>}
            </CardContent>
          </Card>
        </div>

        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t border-gray-200 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-300">
            Cancel
          </Button>

          {isPreviewMode ? (
            <>
              <Button variant="outline" onClick={() => setIsPreviewMode(false)}>
                Edit Again
              </Button>
              <Button onClick={handleSubmit} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Confirm & Submit
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setShowErrors(true);
                if (validateForm()) {
                  setTimeout(() => {
                    const scrollable = document.querySelector(".flex-1.overflow-y-auto.pr-4") as HTMLElement;
                    if (scrollable) {
                      scrollable.scrollTo({ top: 0, behavior: "smooth" });
                    } else {
                      console.warn("Scroll viewport not found");
                    }
                  }, 200);

                  setIsPreviewMode(true);
                } else {
                  showValidationToast(validationErrors);
                }
              }}
              className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
            >
              Preview Before Submit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
